import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { X, Plus, Trash2, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditOrderModal = ({ order, onClose, onSuccess }) => {
  const [items, setItems] = useState([...order.items]);
  const [observations, setObservations] = useState(order.observations || '');
  const [loading, setLoading] = useState(false);
  
  // Add product states
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Search products
  const searchProducts = async (term) => {
    if (term.length < 2) {
      setProducts([]);
      return;
    }

    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const filtered = response.data.filter(p => 
        p.model_name?.toLowerCase().includes(term.toLowerCase()) ||
        p.model?.toLowerCase().includes(term.toLowerCase())
      );
      
      setProducts(filtered.slice(0, 10)); // Limit to 10 results
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    searchProducts(term);
  };

  const selectProduct = (product) => {
    setSelectedProduct(product);
    setSelectedColor(product.colors?.[0] || null);
    setSelectedSize(null);
    setProducts([]);
    setSearchTerm('');
  };

  const addProductToOrder = () => {
    if (!selectedProduct || !selectedColor || !selectedSize) {
      toast.error('Selecciona producto, color y talla');
      return;
    }

    const newItem = {
      variant_id: selectedSize.ean || `${selectedProduct.id}-${selectedColor.color}-${selectedSize.size}`,
      reference: selectedSize.reference || selectedColor.reference || 'N/A',
      ean: selectedSize.ean || '-',
      model_name: selectedProduct.model_name || selectedProduct.model,
      color: selectedColor.color,
      size: selectedSize.size,
      quantity: quantity,
      price: selectedSize.pt_price || selectedSize.pvpr_price || 0
    };

    setItems([...items, newItem]);
    
    // Reset selection
    setSelectedProduct(null);
    setSelectedColor(null);
    setSelectedSize(null);
    setQuantity(1);
    setShowAddProduct(false);
    
    toast.success('Producto añadido al pedido');
  };

  const updateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;
    const updatedItems = [...items];
    updatedItems[index].quantity = newQuantity;
    setItems(updatedItems);
  };

  const removeItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (items.length === 0) {
      toast.error('El pedido debe tener al menos un item');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/orders/${order.id}`,
        {
          items,
          observations: observations.trim() || null,
          total: calculateTotal()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Pedido actualizado exitosamente');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar pedido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Editar Pedido</h2>
            <p className="text-sm text-slate-600">#{order.order_number}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Items List */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <Label className="text-lg font-semibold">Items del Pedido</Label>
              <Button
                type="button"
                onClick={() => setShowAddProduct(!showAddProduct)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
              >
                <Plus className="w-4 h-4" />
                Añadir Producto
              </Button>
            </div>

            {/* Add Product Form */}
            {showAddProduct && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-900 mb-3">Añadir Nuevo Producto</h4>
                
                {/* Search Product */}
                <div className="mb-3">
                  <Label className="text-sm mb-1">Buscar Producto</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      type="text"
                      placeholder="Buscar por nombre..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="pl-10"
                    />
                  </div>
                  
                  {/* Search Results */}
                  {loadingProducts && (
                    <p className="text-sm text-gray-500 mt-2">Buscando...</p>
                  )}
                  {products.length > 0 && (
                    <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                      {products.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => selectProduct(product)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-0"
                        >
                          <p className="font-medium text-sm">{product.model_name || product.model}</p>
                          <p className="text-xs text-gray-500">{product.id}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Product Details */}
                {selectedProduct && (
                  <div className="space-y-3">
                    <div className="bg-white p-3 rounded border border-blue-200">
                      <p className="font-semibold text-sm text-blue-900">{selectedProduct.model_name || selectedProduct.model}</p>
                    </div>

                    {/* Color Selection */}
                    <div>
                      <Label className="text-sm mb-1">Color</Label>
                      <select
                        value={selectedColor?.color || ''}
                        onChange={(e) => {
                          const color = selectedProduct.colors.find(c => c.color === e.target.value);
                          setSelectedColor(color);
                          setSelectedSize(null);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        {selectedProduct.colors?.map((color, idx) => (
                          <option key={idx} value={color.color}>{color.color}</option>
                        ))}
                      </select>
                    </div>

                    {/* Size Selection */}
                    {selectedColor && (
                      <div>
                        <Label className="text-sm mb-1">Talla</Label>
                        <select
                          value={selectedSize?.size || ''}
                          onChange={(e) => {
                            const size = selectedColor.sizes.find(s => s.size === e.target.value);
                            setSelectedSize(size);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="">Selecciona talla</option>
                          {selectedColor.sizes?.map((size, idx) => (
                            <option key={idx} value={size.size}>
                              {size.size} (Stock: {size.stock || 0})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Quantity and Price */}
                    {selectedSize && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm mb-1">Cantidad</Label>
                          <Input
                            type="number"
                            min="1"
                            value={quantity}
                            onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-sm mb-1">Precio</Label>
                          <Input
                            type="text"
                            value={`€${(selectedSize.pt_price || selectedSize.pvpr_price || 0).toFixed(2)}`}
                            disabled
                            className="text-sm bg-gray-50"
                          />
                        </div>
                      </div>
                    )}

                    {/* Add Button */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        type="button"
                        onClick={addProductToOrder}
                        disabled={!selectedSize}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                        size="sm"
                      >
                        Añadir al Pedido
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          setShowAddProduct(false);
                          setSelectedProduct(null);
                          setSelectedColor(null);
                          setSelectedSize(null);
                          setSearchTerm('');
                          setProducts([]);
                        }}
                        variant="outline"
                        size="sm"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3">
              {items.map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{item.model_name}</p>
                    <p className="text-sm text-slate-600">
                      REF: {item.reference} | Color: {item.color} | Talla: {item.size}
                    </p>
                    <p className="text-sm text-blue-600">€{item.price.toFixed(2)} c/u</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      onClick={() => updateQuantity(index, item.quantity - 1)}
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                    >
                      -
                    </Button>
                    <span className="w-12 text-center font-semibold">{item.quantity}</span>
                    <Button
                      type="button"
                      onClick={() => updateQuantity(index, item.quantity + 1)}
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                    >
                      +
                    </Button>
                  </div>
                  <div className="text-right min-w-[80px]">
                    <p className="font-bold text-slate-900">€{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                  <Button
                    type="button"
                    onClick={() => removeItem(index)}
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </div>
              ))}
            </div>

            {items.length === 0 && (
              <p className="text-center text-slate-500 py-8">No hay items en el pedido</p>
            )}
          </div>

          {/* Observations */}
          <div>
            <Label htmlFor="observations">Observaciones</Label>
            <textarea
              id="observations"
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows="3"
              placeholder="Observaciones del pedido..."
            />
          </div>

          {/* Total */}
          <div className="border-t border-slate-200 pt-4">
            <div className="flex justify-between items-center text-xl font-bold">
              <span>Total:</span>
              <span className="text-blue-600">€{calculateTotal().toFixed(2)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              disabled={loading || items.length === 0}
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditOrderModal;